import { DOCUMENT, isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { SAVING_ARENA_API_CONFIG } from '../api/api.config';
import { getPageSeoOverride } from './page-seo-registry';
import { JsonLd } from './structured-data';

export interface PageSeo {
  title: string;
  description?: string;
  /**
   * Canonical path for this page (e.g. `/zaful-discount-codes`). Defaults to the current router URL
   * with its query string stripped. Always resolved against the site origin to an absolute URL —
   * Google requires canonical URLs to be absolute.
   */
  canonicalPath?: string;
  /**
   * Query string to keep on the canonical, e.g. `page=2`.
   *
   * `canonicalPath` deliberately drops everything after `?`, which is what stops a tracking or
   * filter parameter from nominating itself as a separate page. Pagination is the case where the
   * parameter genuinely makes a different page: page 2 of a store list holds different stores, and
   * canonicalising it back to page 1 would tell Google to ignore it — along with every store link
   * only reachable from it. So a page opts in explicitly rather than the rule being relaxed.
   */
  canonicalQuery?: string;
  /** Absolute or site-relative image for OG/Twitter cards. */
  image?: string;
  /** og:type — 'website' (default) or 'article'. */
  type?: 'website' | 'article';
  /**
   * Set true for pages that must not be indexed (thin/incomplete content, unpublished comparisons,
   * arbitrary filter combinations). Emits `noindex, follow` — follow, so link equity still flows.
   */
  noindex?: boolean;
  /**
   * Set true on a URL that no longer exists (a 404, or a retired URL answering 410) so no canonical
   * is emitted at all.
   *
   * A self-referencing canonical on a dead URL is a contradiction — it nominates the URL as the
   * preferred version of itself while the status says it is gone — and it is exactly the mixed
   * signal that keeps a retired address in the index.
   */
  noCanonical?: boolean;
}

const SITE_NAME = 'SavingArena';
const JSON_LD_ATTR = 'data-seo-jsonld';

/**
 * Single owner of every SEO tag on the page.
 *
 * Everything here runs during SERVER rendering, so the tags land in the initial HTML response rather
 * than being patched in by JS afterwards (spec §5). Before this existed the app had no canonical
 * tags, no OG/Twitter tags and no JSON-LD at all, and only 11 of ~40 pages even set a <title> —
 * the store page, the most SEO-critical page on the site, set none.
 *
 * Pages call `apply()` once their data has resolved. On the server, rendering waits for that data
 * before serialising the DOM, so the resolved tags are what crawlers receive.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly doc = inject(DOCUMENT);
  private readonly titleService = inject(Title);
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);
  private readonly config = inject(SAVING_ARENA_API_CONFIG);
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));

  get origin(): string {
    return this.config.siteOrigin;
  }

  /** Builds an absolute URL from a site-relative path. */
  absolute(path: string): string {
    if (/^https?:\/\//i.test(path)) return path;
    const normalized = this.normalizePath(path);
    return `${this.origin}${normalized}`;
  }

  /**
   * Canonicalises a path: drops the query string and hash (so `?source=facebook` and `?utm_*` all
   * collapse onto one canonical URL), lowercases, and strips the trailing slash (spec §6).
   */
  private normalizePath(path: string): string {
    const withoutQuery = String(path || '/')
      .split('#')[0]
      .split('?')[0];
    const lowered = withoutQuery.toLowerCase();
    const trimmed = lowered.replace(/\/+$/, '');
    return trimmed === '' ? '/' : trimmed;
  }

  apply(seo: PageSeo): void {
    const canonicalPath = this.normalizePath(seo.canonicalPath ?? this.router.url);
    const canonicalQuery = (seo.canonicalQuery ?? '').replace(/^\?/, '').trim();
    const canonicalUrl = this.absolute(canonicalPath) + (canonicalQuery ? `?${canonicalQuery}` : '');

    // An admin override for this exact page wins over whatever the component passed in. Applied
    // here rather than in each page so a page is covered simply by setting its SEO at all — see
    // `page-seo-registry`. Only the two fields the admin manages are overridden; canonical, image,
    // robots and JSON-LD stay the page's own business.
    const override = getPageSeoOverride(canonicalPath);
    const title = override?.metaTitle?.trim() || seo.title;
    const description = (override?.metaDescription?.trim() || seo.description || '').trim();

    // The share card a page falls back to when it has no image of its own.
    //
    // 1200x630, NOT the square logo this used to point at — and that file did not exist at all, so
    // every page without its own image advertised a 404 to Facebook, X, WhatsApp and LinkedIn. Those
    // platforms crop to roughly 1.91:1, so a square mark would be cut top and bottom even once the
    // file was there. `logo.png` stays square and stays the Organization JSON-LD logo, which is a
    // different job with different rules.
    const image = seo.image ? this.absolute(seo.image) : `${this.origin}/images/og-image.png`;

    this.titleService.setTitle(title);

    if (description) {
      this.meta.updateTag({ name: 'description', content: description });
    } else {
      this.meta.removeTag("name='description'");
    }

    // follow even when noindex, so link equity keeps flowing through the page.
    this.meta.updateTag({
      name: 'robots',
      content: seo.noindex ? 'noindex, follow' : 'index, follow',
    });

    if (seo.noCanonical) {
      this.removeCanonical();
    } else {
      this.setCanonical(canonicalUrl);
    }

    // Open Graph
    this.meta.updateTag({ property: 'og:site_name', content: SITE_NAME });
    this.meta.updateTag({ property: 'og:type', content: seo.type || 'website' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: image });
    if (description) {
      this.meta.updateTag({ property: 'og:description', content: description });
    }

    // Twitter
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    if (description) {
      this.meta.updateTag({ name: 'twitter:description', content: description });
    }
  }

  /**
   * Toggle just the robots directive without re-applying the whole page SEO. Used for transient
   * states like an open coupon modal (`?coupon=` URL) that must not be indexed — the canonical still
   * points at the clean page, so link equity keeps flowing (`noindex, follow`).
   */
  setNoindex(noindex: boolean): void {
    this.meta.updateTag({ name: 'robots', content: noindex ? 'noindex, follow' : 'index, follow' });
  }

  /**
   * `<link rel="prev">` / `<link rel="next">` for a paginated list.
   *
   * Google stopped using these as an indexing signal in 2019, but Bing still does and they cost
   * nothing. What they mainly buy is a browser hint and a clear statement, in the page itself, that
   * these URLs are one sequence rather than a pile of near-duplicates.
   *
   * Both are cleared when absent. Client-side navigation reuses the document, so leaving the
   * previous page's `next` behind would point at a page that has nothing to do with this one — the
   * same reason `removeCanonical` exists.
   */
  setPaginationLinks(previousUrl: string, nextUrl: string): void {
    const set = (rel: 'prev' | 'next', href: string) => {
      const existing = this.doc.head.querySelector<HTMLLinkElement>(`link[rel='${rel}']`);

      if (!href) {
        existing?.remove();
        return;
      }

      const link = existing ?? this.doc.createElement('link');
      link.setAttribute('rel', rel);
      link.setAttribute('href', href);
      if (!existing) this.doc.head.appendChild(link);
    };

    set('prev', previousUrl);
    set('next', nextUrl);
  }

  /**
   * Tells the browser to start fetching an image it would otherwise find late.
   *
   * The home page's hero art is a CSS `background-image` set from a custom property, and a
   * background image is not discoverable from the HTML: the browser has to receive the document,
   * fetch and parse the stylesheet, build the box, and only then learn the URL. On the home page
   * that image is the Largest Contentful Paint, so those steps sit directly in front of the metric —
   * this is what PageSpeed reports as "LCP request discovery".
   *
   * A preload link in the head moves the request to the moment the HTML arrives. It is emitted
   * during server rendering, so it is in the document the browser first parses rather than added
   * later by script, which would be too late to help.
   *
   * Only one image is ever preloaded. Preloading the whole carousel would put four large downloads
   * ahead of the stylesheet and make the thing it is meant to fix worse.
   */
  preloadImage(url: string): void {
    if (!url) return;

    const head = this.doc.head;
    const existing = head.querySelector<HTMLLinkElement>("link[rel='preload'][data-hero]");
    if (existing?.getAttribute('href') === url) return;

    const link = existing ?? this.doc.createElement('link');
    link.setAttribute('rel', 'preload');
    link.setAttribute('as', 'image');
    link.setAttribute('data-hero', '');
    // The hero spans the viewport, so it is the highest-priority image on the page.
    link.setAttribute('fetchpriority', 'high');
    link.setAttribute('href', url);
    if (!existing) head.appendChild(link);
  }

  private setCanonical(url: string): void {
    const head = this.doc.head;
    let link = head.querySelector<HTMLLinkElement>("link[rel='canonical']");

    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  /**
   * Drops the canonical link entirely.
   *
   * Client-side navigation reuses the same document, so arriving on a dead URL from a live one has
   * to remove the previous page's canonical — leaving it would nominate the wrong page.
   */
  private removeCanonical(): void {
    this.doc.head.querySelector("link[rel='canonical']")?.remove();
  }

  /**
   * Replaces all page-level JSON-LD with `blocks`.
   *
   * Client-side navigation reuses the same document, so stale schema from the previous page has to
   * be cleared first — otherwise a store's Offer list would linger on the blog page you navigated to.
   */
  setJsonLd(blocks: (JsonLd | null | undefined)[]): void {
    this.clearJsonLd();

    const valid = blocks.filter((block): block is JsonLd => Boolean(block));
    if (!valid.length) return;

    for (const block of valid) {
      const script = this.doc.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute(JSON_LD_ATTR, '');
      // JSON.stringify escapes nothing dangerous here (the payload is our own data), but `<` must be
      // escaped so a value containing "</script>" cannot break out of the tag.
      script.textContent = JSON.stringify(block).replace(/</g, '\\u003c');
      this.doc.head.appendChild(script);
    }
  }

  private clearJsonLd(): void {
    const existing = this.doc.head.querySelectorAll(`script[${JSON_LD_ATTR}]`);
    existing.forEach((node) => node.remove());
  }

  /** True while rendering on the server — lets callers skip browser-only work. */
  get renderingOnServer(): boolean {
    return this.isServer;
  }
}
